/**
 * Lead Volume per Originating Firm — the wire shapes (#1051, Firm Success PRD
 * Phase 2). The worker computes (apps/worker/src/firm-success/lead-volume.ts);
 * the web draws (apps/web/src/routes/client-success/firm-success/lead-volume.tsx);
 * lf-ops reads the same routes (get_lead_volume, list_lead_volume_runs,
 * list_lead_volume_signals) and changes the settings (set_lead_volume_settings).
 *
 * Every response carries the run it reads — its as_of (the Pacific date and
 * time the read began) — because a count is true only as of its read
 * (PRD 2.1.5, 2.2.12).
 */

import { z } from "zod";

/** The change-signal settings (PRD 2.3 requirement 1). Bounds keep a value
 *  inside what the method can evaluate; they say nothing about a good value. */
export const LeadVolumeSettingsSchema = z
  .object({
    windowDays: z.number().int().min(28).max(1095),
    minPriorDays: z.number().int().min(7).max(1095),
    lowPercentile: z.number().min(0).max(50),
    highPercentile: z.number().min(50).max(100),
    persistDays: z.number().int().min(1).max(90),
    minLowMark: z.number().min(0).max(10000),
    quietMinReferralDays: z.number().int().min(2).max(10000),
  })
  .strict();
export type LeadVolumeSettingsWire = z.infer<typeof LeadVolumeSettingsSchema>;

/** PUT /api/firm-success/lead-volume/settings — any subset of the settings,
 *  merged onto those in force, and why. */
export const LeadVolumeSettingsUpdateRequestSchema = z
  .object({
    settings: LeadVolumeSettingsSchema.partial(),
    reason: z.string().trim().min(1).max(1000),
  })
  .strict();
export type LeadVolumeSettingsUpdateRequest = z.infer<typeof LeadVolumeSettingsUpdateRequestSchema>;

export const LeadVolumeSettingsRowSchema = z.object({
  version: z.number().int(),
  settings: LeadVolumeSettingsSchema,
  setAt: z.string(),
  setBy: z.string(),
  reason: z.string(),
});

export const LeadVolumeRunMetaSchema = z.object({
  runId: z.number().int(),
  runDay: z.string(),
  asOf: z.string(),
  readFinishedAt: z.string(),
  trigger: z.enum(["scheduled", "manual"]),
  triggeredBy: z.string(),
  rowsRead: z.number().int(),
});
export type LeadVolumeRunMeta = z.infer<typeof LeadVolumeRunMetaSchema>;

export const LeadVolumeChecksSchema = z.object({
  firmDailySumEqualsTotal: z.object({ firmsWhereDiffer: z.array(z.string()), equal: z.boolean() }),
  leadsPlusTransfersEqualsReferrals: z.object({ firmsWhereDiffer: z.array(z.string()), equal: z.boolean() }),
  firmsPlusNoFirmEqualsSystem: z.object({
    firmsSum: z.number().int(),
    noFirm: z.number().int(),
    rowsWithADay: z.number().int(),
    equal: z.boolean(),
  }),
  notRun: z.array(z.object({ check: z.string(), why: z.string() })),
});

export const LEAD_VOLUME_RANKS = [
  "none",
  "low",
  "low_persisting",
  "high",
  "growth",
  "not_enough_history",
  "too_small_to_rank",
] as const;
export const LeadVolumeRankSchema = z.enum(LEAD_VOLUME_RANKS);
export type LeadVolumeRank = z.infer<typeof LeadVolumeRankSchema>;

export const LeadVolumeNumbersSchema = z.object({
  today: z.number().int(),
  yesterday: z.number().int(),
  last7: z.number().int(),
  prior7: z.number().int(),
  last28: z.number().int(),
  prior28: z.number().int(),
  total: z.number().int(),
  leads: z.number().int(),
  transfers: z.number().int(),
  lastDay: z.string().nullable(),
  daysSinceLast: z.number().int().nullable(),
});
export type LeadVolumeNumbers = z.infer<typeof LeadVolumeNumbersSchema>;

/** Yesterday's state for a scope: its rank and whether it is quiet. */
export const LeadVolumeStateSchema = z.object({
  day: z.string().nullable(),
  rank: LeadVolumeRankSchema,
  quiet: z.boolean(),
});

export const LeadVolumeFirmRowSchema = z.object({
  firmId: z.string(),
  name: z.string(),
  firstDay: z.string(),
  lastDay: z.string(),
  referrals: z.number().int(),
  leads: z.number().int(),
  transfers: z.number().int(),
  numbers: LeadVolumeNumbersSchema,
  state: LeadVolumeStateSchema,
});

/** One note on a kept signal (#1246, migration 0021): append-only, with who wrote it — the
 *  request's operator email; through lf-ops, the door's one identity — and when (ISO-8601 UTC). */
export const LeadVolumeSignalNoteSchema = z.object({
  noteId: z.number().int(),
  note: z.string(),
  writtenBy: z.string(),
  writtenAt: z.string(),
});
export type LeadVolumeSignalNote = z.infer<typeof LeadVolumeSignalNoteSchema>;

export const LeadVolumeSignalSchema = z.object({
  signalId: z.number().int(),
  day: z.string(),
  scope: z.string(),
  scopeName: z.string(),
  kind: z.enum(["low", "low_persisting", "high", "growth", "quiet"]),
  measured: z.record(z.string(), z.unknown()),
  settingsVersion: z.number().int(),
  runId: z.number().int(),
  recordedAt: z.string(),
  /** The newest note's text; null until one is written. */
  note: z.string().nullable(),
  /** Every note on the signal, oldest first. */
  notes: z.array(LeadVolumeSignalNoteSchema),
});
export type LeadVolumeSignal = z.infer<typeof LeadVolumeSignalSchema>;

/** GET /api/firm-success/lead-volume — the overview: the run, its checks, the
 *  settings in force, LegalFlare first, then each Originating Firm by total. */
export const LeadVolumeOverviewResponseSchema = z.object({
  run: LeadVolumeRunMetaSchema.extend({
    rowsWithNoDay: z.number().int(),
    rowsWithNoFirm: z.number().int(),
    restampedUtcToPacific: z.number().int(),
    checks: LeadVolumeChecksSchema,
  }),
  today: z.string(),
  settings: LeadVolumeSettingsRowSchema,
  legalflare: z.object({
    numbers: LeadVolumeNumbersSchema,
    state: LeadVolumeStateSchema,
    firmsSendingNow: z.number().int(),
    firmsSendingState: LeadVolumeStateSchema,
    topFive: z.array(z.object({ firmId: z.string(), name: z.string() })),
    started: z.array(z.object({ firmId: z.string(), name: z.string(), firstDay: z.string() })),
    stopped: z.array(z.object({ firmId: z.string(), name: z.string(), lastDay: z.string() })),
  }),
  noOriginatingFirm: z
    .object({
      referrals: z.number().int(),
      leads: z.number().int(),
      transfers: z.number().int(),
      firstDay: z.string(),
      lastDay: z.string(),
    })
    .nullable(),
  firms: z.array(LeadVolumeFirmRowSchema),
  signalsYesterday: z.array(LeadVolumeSignalSchema),
});
export type LeadVolumeOverviewResponse = z.infer<typeof LeadVolumeOverviewResponseSchema>;

/** One scope's days, compact: arrays index-aligned from `start`. */
export const LeadVolumeSeriesSchema = z.object({
  scope: z.string(),
  name: z.string(),
  start: z.string(),
  n: z.array(z.number()),
  leads: z.array(z.number()).nullable(),
  transfers: z.array(z.number()).nullable(),
  line7: z.array(z.number().nullable()),
  line28: z.array(z.number().nullable()),
  rank: z.array(LeadVolumeRankSchema),
  quiet: z.array(z.boolean()),
});
export type LeadVolumeSeries = z.infer<typeof LeadVolumeSeriesSchema>;

/** GET /api/firm-success/lead-volume/series?scope=… — one scope, for lf-ops. */
export const LeadVolumeSeriesResponseSchema = z.object({
  run: LeadVolumeRunMetaSchema,
  today: z.string(),
  settingsVersion: z.number().int(),
  series: LeadVolumeSeriesSchema,
});
export type LeadVolumeSeriesResponse = z.infer<typeof LeadVolumeSeriesResponseSchema>;

/** GET /api/firm-success/lead-volume/page — everything the view draws. */
export const LeadVolumePageResponseSchema = LeadVolumeOverviewResponseSchema.extend({
  series: z.object({
    legalflare: LeadVolumeSeriesSchema,
    firmsSending: LeadVolumeSeriesSchema,
    topFive: z.object({ start: z.string(), topN: z.array(z.number()), othersN: z.array(z.number()) }),
    firms: z.array(LeadVolumeSeriesSchema),
  }),
});
export type LeadVolumePageResponse = z.infer<typeof LeadVolumePageResponseSchema>;
